import * as React from 'react';

import { ZonedDateTime } from '@internationalized/date';
import {
  Avatar,
  Col,
  LocalizedDate,
  Row,
  TouchButton,
  cn,
} from '@rematter/ui-web';
import { LucideImageOff, LucideTag } from 'lucide-react';

import { ImageCarousel } from './ImageCarousel';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type ImageFile = {
  id: string;
  fileName: string;
  path?: string;
  mimeType: string;
};

type CardVariant = 'compact' | 'gallery';

type TicketSidebarCardProps = {
  id: string;
  documentNumber: string;
  contactType: string;
  contactName?: string | null;
  subtitle: string | null;
  notesPreview: string | null;
  lineItemCount: number;
  date: ZonedDateTime;
  isSelected: boolean;
  onSelect: (id: string) => void;
  imageFiles?: ImageFile[];
  statusBadge?: React.ReactNode;
  avatarUrl?: string;
  compact?: boolean;
  className?: string;
};

type ImageAreaProps = { files: ImageFile[] };

//
// ⚛️ ⚛️ ⚛️ IMAGE AREA ⚛️ ⚛️ ⚛️
//
const ImageArea = ({ files }: ImageAreaProps) => (
  <div className="@ImageArea flex aspect-video w-full items-center justify-center overflow-hidden rounded-md rounded-b-none bg-neutral/10">
    {files.length > 0 ? (
      <ImageCarousel files={files} className="h-full w-full" />
    ) : (
      <LucideImageOff
        size={48}
        className="text-foreground/20"
        strokeWidth={0.5}
      />
    )}
  </div>
);

//
// ⚛️ ⚛️ ⚛️ TICKET SIDEBAR CARD ⚛️ ⚛️ ⚛️
//
export const TicketSidebarCard = React.memo(
  ({
    id,
    documentNumber,
    contactType,
    contactName,
    subtitle,
    notesPreview,
    lineItemCount,
    date,
    isSelected,
    onSelect,
    imageFiles,
    statusBadge,
    avatarUrl,
    compact = false,
    className,
  }: TicketSidebarCardProps) => {
    const t = useTranslations();

    const displayName =
      contactName ?? t.scaleTicketPro.unidentified(contactType);
    const variant: CardVariant =
      imageFiles !== undefined && !compact ? 'gallery' : 'compact';

    return (
      <Col
        className={cn(
          variant === 'compact' && 'border-b border-neutral/10',
          variant === 'gallery' &&
            'cursor-pointer rounded-md hover:outline-2 hover:outline-primary',
          className,
        )}
        onClick={() => onSelect(id)}
      >
        {variant === 'gallery' && imageFiles && (
          <ImageArea files={imageFiles} />
        )}

        <TouchButton
          isSelected={isSelected}
          onPress={() => onSelect(id)}
          variant="subtle"
          color="primary"
          align="left"
          className={cn(
            '@TicketSidebarCard relative h-auto w-full flex-1 rounded-lg border-2 p-0',
            variant === 'compact' &&
              'after:absolute after:right-0 after:bottom-0 after:left-0 after:h-px after:bg-neutral/15 last:after:hidden',
            variant === 'gallery' &&
              'rounded-t-none border-transparent! bg-white',
            '[&>*]:h-full',
          )}
        >
          <Row
            gap2
            className={cn(
              'h-full w-full',
              variant === 'compact' && 'gap-0.5 px-2 py-1.5',
            )}
          >
            {variant === 'compact' &&
              !compact &&
              (imageFiles && imageFiles.length > 0 ? (
                <img
                  src={imageFiles[0].path}
                  alt={imageFiles[0].fileName}
                  className={cn(
                    'size-10 shrink-0 rounded-md object-cover',
                    isSelected && 'opacity-75',
                  )}
                />
              ) : (
                <Avatar
                  name={displayName}
                  size="xl"
                  variant="profile"
                  imageUrl={avatarUrl}
                  className={cn('shrink-0', isSelected && 'bg-white/75')}
                />
              ))}

            <Col yBetween className="h-full min-w-0 flex-1 px-2 py-1.5">
              <Col>
                <Row
                  gap2
                  xBetween
                  className={cn(
                    'font-medium text-neutral uppercase',
                    isSelected && 'text-white/75',
                  )}
                >
                  <span className="truncate">
                    #{String(documentNumber).padStart(6, '0')}
                  </span>
                  {statusBadge}
                </Row>
                {notesPreview && (
                  <span
                    className={cn(
                      'truncate text-xs text-neutral/70',
                      isSelected && 'text-white/60',
                    )}
                  >
                    {notesPreview}
                  </span>
                )}
              </Col>

              <span
                className={cn(
                  'truncate font-bold text-foreground',
                  !contactName && 'font-normal text-neutral/60',
                  isSelected && 'text-white',
                )}
              >
                {displayName}
              </span>

              <span
                className={cn(
                  'min-h-[12px] truncate text-xs text-neutral/80',
                  isSelected && 'text-white/75',
                )}
              >
                {subtitle || ' '}
              </span>

              <Row yCenter xBetween>
                <Row
                  gap1
                  yCenter
                  className={cn(
                    'text-xs text-neutral/60',
                    isSelected && 'text-white/75',
                  )}
                >
                  <LucideTag size={12} />
                  {lineItemCount}
                </Row>
                <span
                  className={cn(
                    'text-xs font-normal text-neutral/60',
                    isSelected && 'text-white/75',
                  )}
                >
                  <LocalizedDate
                    date={date}
                    dateStyle="medium"
                    wrapTriggerWithButton={false}
                  />
                </span>
              </Row>
            </Col>
          </Row>
        </TouchButton>
      </Col>
    );
  },
);
